import React, { useEffect, useState } from 'react';
import { Layout, Card, Button, message, Space, Typography } from 'antd';
import { ArrowLeftOutlined, CalendarOutlined, FileTextOutlined, StopOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import MainHeader from './MainHeader';
import api from '../api';

const { Content } = Layout;
const { Text } = Typography;

export default function SalaryCalculationLogic() {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('calendar');
  const [fixedDaysCalcRule, setFixedDaysCalcRule] = useState('deduct_extra_days');

  const fetchSettings = async () => {
    try {
      const resp = await api.get('/admin/settings/salary');
      const s = resp?.data?.settings || {};
      const apiMode = s.payableDaysMode || 'calendar_month';
      
      // Map API value to Frontend UI value
      let uiMode = 'calendar';
      if (apiMode === 'calendar_month') uiMode = 'calendar';
      else if (apiMode === 'every_30') uiMode = 'fixed_30';
      else if (apiMode === 'every_28') uiMode = 'fixed_28';
      else if (apiMode === 'every_26') uiMode = 'fixed_26';
      else if (apiMode === 'exclude_weekly_offs') uiMode = 'exclude_weekly_offs';
      
      setMode(uiMode);
      setFixedDaysCalcRule(s.fixedDaysCalcRule || s.thirtyDaysCalcRule || 'deduct_extra_days');
    } catch (e) {
      message.error('Failed to load salary calculation settings');
    }
  };

  useEffect(() => { fetchSettings(); }, []);

  const save = async () => {
    try {
      setLoading(true);
      
      const respGet = await api.get('/admin/settings/salary');
      const currentSettings = respGet?.data?.settings || {};

      // Map Frontend UI value to API value
      let apiMode = 'calendar_month';
      if (mode === 'calendar') apiMode = 'calendar_month';
      else if (mode === 'fixed_30') apiMode = 'every_30';
      else if (mode === 'fixed_28') apiMode = 'every_28';
      else if (mode === 'fixed_26') apiMode = 'every_26';
      else if (mode === 'exclude_weekly_offs') apiMode = 'exclude_weekly_offs';

      const payload = {
        ...currentSettings,
        payableDaysMode: apiMode,
        fixedDaysCalcRule,
        thirtyDaysCalcRule: fixedDaysCalcRule
      };
      const resp = await api.put('/admin/settings/salary', payload);
      if (resp.data?.success) {
        message.success('Salary calculation logic saved');
        const s = resp.data.settings || {};
        setFixedDaysCalcRule(s.fixedDaysCalcRule || s.thirtyDaysCalcRule || fixedDaysCalcRule);
      } else {
        message.error(resp.data?.message || 'Failed to save');
      }
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const options = [
    {
      value: 'calendar',
      label: 'Calendar Month',
      desc: 'Calculates payable days based on the actual number of days in the month (e.g., March = 31, April = 30).',
      icon: <CalendarOutlined style={{ fontSize: '20px', color: '#1677ff' }} />,
      badge: 'Standard'
    },
    {
      value: 'fixed_30',
      label: 'Every Month 30 Days',
      desc: 'Considers every month as exactly 30 payable days, regardless of the calendar length.',
      icon: <FileTextOutlined style={{ fontSize: '20px', color: '#722ed1' }} />,
      badge: 'Commercial'
    },
    {
      value: 'fixed_28',
      label: 'Every Month 28 Days',
      desc: 'Considers every month as exactly 28 payable days (often used in weekly-aligned cycles).',
      icon: <FileTextOutlined style={{ fontSize: '20px', color: '#eb2f96' }} />,
      badge: 'Special'
    },
    {
      value: 'fixed_26',
      label: 'Every Month 26 Days',
      desc: 'Considers every month as exactly 26 payable days (standard for 6-day work weeks).',
      icon: <FileTextOutlined style={{ fontSize: '20px', color: '#fa8c16' }} />,
      badge: '6-Day Work'
    },
    {
      value: 'exclude_weekly_offs',
      label: 'Exclude Weekly Offs',
      desc: 'Calculates payable days by subtracting weekly offs (Sundays/Saturdays) from the calendar days.',
      icon: <StopOutlined style={{ fontSize: '20px', color: '#52c41a' }} />,
      badge: 'Dynamic'
    }
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar collapsed={collapsed} />
      <Layout style={{ marginLeft: collapsed ? 80 : 200, height: '100vh', overflow: 'hidden', transition: 'margin-left 0.2s' }}>
        <MainHeader 
          collapsed={collapsed} 
          setCollapsed={setCollapsed} 
          title="Salary Calculation Logic" 
        />
        <Content style={{ margin: '24px 16px', padding: 24, background: '#f5f5f5', height: 'calc(100vh - 64px - 48px)', overflow: 'auto' }}>
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            
            {/* Toolbar Row */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button 
                type="text" 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate('/settings')}
                style={{ fontWeight: 600, color: '#475569' }}
                shape="round"
              >
                Back to Settings
              </Button>
            </div>

            {/* Content Card */}
            <Card 
              className="sales-content-card" 
              style={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }} 
              bodyStyle={{ padding: '24px' }}
            >
              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>Select Payable Days & Work Hours</div>
                <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>
                  What is the effective payable days per month and work hours per day in your organization?
                </div>
              </div>

              {/* Dynamic Option Cards */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {options.map((opt) => {
                  const isSelected = mode === opt.value;
                  return (
                    <div 
                      key={opt.value}
                      onClick={() => setMode(opt.value)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '16px 20px',
                        borderRadius: '12px',
                        border: isSelected ? '2px solid #1677ff' : '1px solid #e2e8f0',
                        backgroundColor: isSelected ? '#f0f7ff' : '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        boxShadow: isSelected ? '0 4px 12px rgba(22, 119, 255, 0.08)' : '0 2px 4px rgba(0,0,0,0.02)',
                        position: 'relative'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '8px',
                          backgroundColor: isSelected ? '#e6f4ff' : '#f8fafc',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          transition: 'background-color 0.2s'
                        }}>
                          {opt.icon}
                        </div>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontWeight: '700', fontSize: '14px', color: '#1e293b' }}>{opt.label}</span>
                            {opt.badge && (
                              <span style={{
                                fontSize: '9px',
                                fontWeight: '700',
                                padding: '2px 8px',
                                borderRadius: '10px',
                                textTransform: 'uppercase',
                                color: isSelected ? '#0050b3' : '#64748b',
                                backgroundColor: isSelected ? '#bae0ff' : '#f1f5f9',
                                letterSpacing: '0.5px'
                              }}>
                                {opt.badge}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '3px', lineHeight: '1.4' }}>{opt.desc}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '16px' }}>
                        <div style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          border: isSelected ? '6px solid #1677ff' : '2px solid #cbd5e1',
                          backgroundColor: '#fff',
                          transition: 'all 0.2s ease',
                          flexShrink: 0
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Fixed Days Calculation Rule Sub-Options */}
              {['fixed_30', 'fixed_28', 'fixed_26'].includes(mode) && (
                <div style={{
                  marginTop: '20px',
                  padding: '20px',
                  backgroundColor: '#fafafa',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px'
                }}>
                  <div style={{ fontWeight: '700', fontSize: '14px', color: '#1e293b', marginBottom: '4px' }}>
                    Fixed Days Calculation Rule (For 31-Day Months)
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '16px' }}>
                    Choose how payable days are calculated when calendar month days exceed the fixed base rate:
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div 
                      onClick={() => setFixedDaysCalcRule('deduct_extra_days')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '14px 16px',
                        borderRadius: '10px',
                        border: fixedDaysCalcRule === 'deduct_extra_days' ? '2px solid #1677ff' : '1px solid #e2e8f0',
                        backgroundColor: fixedDaysCalcRule === 'deduct_extra_days' ? '#e6f4ff' : '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '13px', color: '#1e293b' }}>
                          Rule 1 (Default): Fixed Base Days (Payable Days + Absent = Base Days)
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                          Deducts extra calendar days in 31-day months (e.g. 28 earned - 1 = 27 Payable Days + 3 Absents = 30).
                        </div>
                      </div>
                      <div style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        border: fixedDaysCalcRule === 'deduct_extra_days' ? '5px solid #1677ff' : '2px solid #cbd5e1',
                        backgroundColor: '#fff',
                        flexShrink: 0
                      }} />
                    </div>

                    <div 
                      onClick={() => setFixedDaysCalcRule('actual_earned_days')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '14px 16px',
                        borderRadius: '10px',
                        border: fixedDaysCalcRule === 'actual_earned_days' ? '2px solid #1677ff' : '1px solid #e2e8f0',
                        backgroundColor: fixedDaysCalcRule === 'actual_earned_days' ? '#e6f4ff' : '#fff',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '13px', color: '#1e293b' }}>
                          Rule 2: Pay Actual Earned Days (Payable Days = Present + WO + Leaves)
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                          Does not deduct extra calendar days in 31-day months (e.g. 24 Present + 4 WO = 28 Payable Days + 3 Absents = 31).
                        </div>
                      </div>
                      <div style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        border: fixedDaysCalcRule === 'actual_earned_days' ? '5px solid #1677ff' : '2px solid #cbd5e1',
                        backgroundColor: '#fff',
                        flexShrink: 0
                      }} />
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons Row */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '32px', paddingTop: '20px', borderTop: '1px solid #f1f5f9' }}>
                <Space size={12}>
                  <Button 
                    shape="round" 
                    onClick={() => navigate('/settings')}
                    style={{ fontWeight: '500', minWidth: '90px' }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="primary" 
                    shape="round" 
                    loading={loading} 
                    onClick={save}
                    style={{ fontWeight: '600', minWidth: '100px', boxShadow: '0 2px 6px rgba(22, 119, 255, 0.15)' }}
                  >
                    Save Changes
                  </Button>
                </Space>
              </div>

            </Card>
          </Space>
        </Content>
      </Layout>
    </Layout>
  );
}
